<template>
    <DialogLayout :class-name="'resume-result-dialog'" @close="handleClose">
        <template #title>{{ props.title }}</template>
        <template #default>
            <el-form ref="formRef" :model="formData" :rules="rules" :label-position="'left'" :label-width="'100px'"
                class="resume-result-form">
                <el-form-item :label="t('candidate.result')" prop="result">
                    <el-radio-group v-model="formData.result" :disabled="isResultDisabled">
                        <el-radio :value="1">合格</el-radio>
                        <el-radio :value="2">不合格</el-radio>
                    </el-radio-group>
                </el-form-item>

                <el-form-item :label="t('candidate.comment')" prop="comment" required>
                    <el-input v-model="formData.comment" type="textarea" :placeholder="t('candidate.commentPlaceholder')" :rows="4" maxlength="500"
                        show-word-limit />
                </el-form-item>
            </el-form>
        </template>
        <template #footer>
            <el-button class="confirm-button-default" @click="handleCancel">
                {{ t('common.cancel') }}
            </el-button>
            <el-button type="primary" :loading="submitting" @click="handleConfirm">
                {{ confirmButtonText }}
            </el-button>
        </template>
    </DialogLayout>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import DialogLayout from './dialog-layout.vue'
import type { ResumeResultData, ResumeResultDialogProps } from './resume-result-dialog-types'
import { submitInterviewEvaluationApi, updateInterviewEvaluationApi } from '@/api/candidate'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const props = defineProps<ResumeResultDialogProps>()

const emit = defineEmits<{
    (e: 'confirm', data: ResumeResultData): void
    (e: 'cancel'): void
    (e: 'close'): void
}>()

const formRef = ref<FormInstance>()
const submitting = ref(false)

// 按钮文本
const confirmButtonText = computed(() => {
    return props.data ? t('common.save') : t('common.confirm1')
})

// 表单数据
const formData = reactive<ResumeResultData>({
    result: undefined,
    comment: ''
})

// 如果有回显数据，结果不可更改
const isResultDisabled = computed(() => {
    return props.data?.result !== undefined && props.data?.result !== null
})

// 表单验证规则
const rules = computed<FormRules>(() => ({
    comment: [
        { required: true, message: t('candidate.pleaseEnterComment'), trigger: 'blur' }
    ]
}))

// 初始化表单数据
onMounted(() => {
    if (props.data) {
        formData.result = props.data.result
        formData.comment = props.data.comment || ''
    } else {
        formData.result = 1
        formData.comment = ''
    }
})

// 确认
const handleConfirm = async () => {
    if (!formRef.value) return

    try {
        // 先验证表单，如果验证失败会抛出错误，不会继续执行
        await formRef.value.validate()
    } catch (error: unknown) {
        // 表单验证失败，不显示额外提示（Element Plus 会自动显示），直接返回，不提交接口
        if (error && typeof error === 'object' && 'fields' in error) {
            return
        }
        // 其他验证错误也直接返回
        return
    }

    // 验证通过后，才执行 API 调用
    try {
        submitting.value = true

        // 调用 API 提交/更新评估结果
        const submitApi = props.data ? updateInterviewEvaluationApi : submitInterviewEvaluationApi
        await submitApi({
            interviewId: props.interviewId,
            stepId: props.stepId,
            type: props.type,
            result: formData.result!,
            comments: formData.comment || ''
        })

        // API 调用成功，将结果传递给父组件并关闭对话框
        const resultData: ResumeResultData = {
            result: formData.result,
            comment: formData.comment
        }
        
        ElMessage.success(t('common.saveSuccess'))
        submitting.value = false
        // emit('confirm') 会自动关闭对话框，Promise 会 resolve
        emit('confirm', resultData)
    } catch (error: unknown) {
        submitting.value = false
        
        // API 调用失败
        const errorMessage = error && typeof error === 'object' && 'message' in error 
            ? String(error.message) 
            : t('common.saveFailed')
        console.error('提交失败:', error)
        ElMessage.error(errorMessage)
    }
}

// 取消
const handleCancel = () => {
    emit('cancel')
}

// 关闭
const handleClose = () => {
    emit('close')
}
</script>

<style scoped lang="scss">
.resume-result-form {

    :deep(.el-form-item__label) {
        font-size: 14px;
        font-weight: 500;
        color: #212429;
        line-height: 35px;
        text-align: right;
        padding-right: 12px;
        margin-bottom: 0px;

        &::before {
            display: none;
        }
    }

    :deep(.el-radio-group) {
        display: flex;
        gap: 20px;
    }

    :deep(.el-radio) {
        margin-right: 0;

        .el-radio__label {
            font-size: 14px;
            color: #495057;
            padding-left: 8px;
        }
    }

    :deep(.el-textarea) {
        .el-textarea__inner {
            font-size: 14px;
            color: #495057;
            line-height: 22px;
            resize: none;

            &::placeholder {
                color: #C9D3D9;
            }
        }
    }

    :deep(.el-input__count) {
        font-size: 12px;
        color: #86909C;
        background: transparent;
    }
}

:deep(.resume-result-dialog) {
    width: 512px;

    

}</style>

<style lang="scss">
.resume-result-dialog {
    width: 512px !important;
    .dialog-footer {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
    
    
            .el-button {
                min-width: 80px;
            }
        }
}
 //.dialog-footer {
    //padding: 0px 30px 20px 30px!important;
 //}
</style>

